import { ProfileTab } from "./ProfileTab/index";
import { Button, Tabs, TabsProps, Skeleton, MenuProps, Dropdown } from "antd";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { HistoryTab } from "./HistoryTab";
import { MoreOutlined } from "@ant-design/icons";
import { useDeleteUser } from "../../hooks/useDeleteUser";
import { deleteUser } from "../../../../acions/users";
import { useDefaultDeleteModal } from "../../../../lib/hooks/defaultDeleteModal";
import { useCanModify } from "../../hooks/useCanModify";
import { useCanAction } from "../../hooks/useCanAction";
import { useFetchUser } from "../../hooks/useFetchUser";
import { IOption } from "../../../../types/Option";
import { useHasDeepPaymentProviderIntegrationAccess } from "../../../../lib/hooks/useHasDeepPaymentProviderIntegrationAccess";
import { usePermissions } from "../../../../lib/hooks/usePermissions";
import USER_ROLES from "../../../../types/UserRoles";
import { PaymentProvidersTab } from "./PaymentProvidersTab";

type UserContentProps = {
  userId: string | null;
  onOpenBlockModal: (userId: string | null, isBlockValue: boolean) => void;
  setIsEditUser: (value: boolean) => void;
  isEditUser: boolean;
  roles: IOption[];
  reloadData: () => void;
  setHeaderTitleOnLoad: (value: any) => void;
};

export const UserContent = ({
  userId,
  onOpenBlockModal,
  setIsEditUser,
  isEditUser,
  roles,
  reloadData,
  setHeaderTitleOnLoad
}: UserContentProps) => {
  const { canModify } = useCanModify();

  const { canAction } = useCanAction();

  const [isSaveClicked, setIsSaveClicked] = useState<boolean>(false);

  const [activeTab, setActiveTab] = useState<string>("1");

  const { openDeleteModal } = useDefaultDeleteModal(deleteUser, reloadData);

  const onSave = useCallback(() => setIsSaveClicked(true), []);

  const { user, isLoading } = useFetchUser(userId, roles, setHeaderTitleOnLoad);

  const { isLoading: isLoadingHasAccess, onDelete } = useDeleteUser(openDeleteModal);

  const { hasAccessByController } = usePermissions("PaymentProviderCustomers");

  const { canViewDeepPaymentProviderIntegration } = useHasDeepPaymentProviderIntegrationAccess();

  const tabItems: TabsProps["items"] = [
    {
      key: "1",
      label: "Profile",
      children: (
        <ProfileTab
          userId={userId}
          user={user}
          isEdit={isEditUser}
          roles={roles}
          setIsSaveClicked={setIsSaveClicked}
          isSaveClicked={isSaveClicked}
          reloadData={reloadData}
          isLoading={isLoading}
        />
      )
    },
    {
      key: "2",
      label: "History",
      children: <HistoryTab userId={userId} />,
      disabled: isEditUser
    }
  ];

  if (canViewDeepPaymentProviderIntegration && user?.role === USER_ROLES.PORTAL_USER) {
    tabItems.push({
      key: "3",
      label: "Payment Providers",
      children: <PaymentProvidersTab userId={userId} />,
      disabled: isEditUser || !hasAccessByController("GetSingle")
    });
  }

  const onToggleBlock = useCallback((value: boolean) => onOpenBlockModal(userId, value), [onOpenBlockModal, userId]);

  const getBlockUnblockBtn = useCallback(
    (key: string, label: string, isBlocked: boolean) => ({
      key,
      label: <div onClick={() => onToggleBlock(isBlocked)}>{label}</div>
    }),
    [onToggleBlock]
  );

  const onDeleteClick = useCallback(() => onDelete(user.id), [onDelete, user]);

  const onEdit = useCallback(() => setIsEditUser(true), [setIsEditUser]);

  const getFooterGroup: ReactNode = useMemo(() => {
    const items: MenuProps["items"] = [];

    user?.status !== 3 &&
      canAction(user?.role, user?.id, "Block") &&
      items.push(getBlockUnblockBtn("3", `${user?.status === 1 ? "Block" : "Unblock"} User`, user?.status === 1));

    canAction(user?.role, user?.id, "Delete") &&
      items.push({
        key: "4",
        label: <div onClick={onDeleteClick}>Delete User</div>
      });

    if (!isEditUser) {
      let editBtn: ReactNode = <div />;

      if (canModify(user?.role)) {
        editBtn = (
          <Button shape="round" onClick={onEdit} className="mr-4">
            Edit Profile
          </Button>
        );
      }

      return (
        <div>
          {activeTab === "1" && editBtn}
          {items.length !== 0 && (
            <Dropdown menu={{ items }} trigger={["click"]} disabled={isLoadingHasAccess}>
              <Button shape="circle" icon={<MoreOutlined />} style={{ minWidth: "unset" }} />
            </Dropdown>
          )}
        </div>
      );
    } else {
      return (
        <div>
          <Button shape="round" type="primary" onClick={onSave} disabled={isSaveClicked} loading={isSaveClicked}>
            Save Changes
          </Button>
        </div>
      );
    }
  }, [
    user,
    canAction,
    getBlockUnblockBtn,
    onDeleteClick,
    isEditUser,
    canModify,
    activeTab,
    isLoadingHasAccess,
    onEdit,
    isSaveClicked,
    onSave
  ]);

  const onChangeTab = useCallback((key: string) => setActiveTab(key), []);

  return (
    <>
      <div className="sidebar-body user-content">
        <Tabs items={tabItems} defaultActiveKey="1" onChange={onChangeTab} />
      </div>
      <div className="sidebar-footer px-6 py-6">
        {!isLoading ? (
          getFooterGroup
        ) : (
          <div className="mt-2 mb-2">
            <Skeleton active round paragraph={{ rows: 1, width: "325px" }} title={false} />
          </div>
        )}
      </div>
    </>
  );
};
